import { Refresh01Icon } from '@hugeicons/core-free-icons';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { QueryState } from '@/components/feedback/query-state';
import { SkeletonKpi } from '@/components/feedback/skeletons';
import {
  buildTiles,
  type Source,
  type TileSources,
  tileOrder,
} from '@/components/home/home-tiles';
import { BoardState } from '@/components/metrics/board-state';
import {
  type IntegrityCell,
  IntegrityStrip,
} from '@/components/metrics/integrity-strip';
import { KpiBand, TileGrid } from '@/components/metrics/kpi-tile';
import { SwitchStrip } from '@/components/metrics/switch-strip';
import { PageHeader } from '@/components/page-header';
import { Icon } from '@/components/primitives/icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDateTime, relativeTime } from '@/lib/format';
import {
  ageMs,
  boardAlerts,
  HOUR,
  jobState,
  MINUTE,
  type OpsStatus,
} from '@/lib/home-model';
import { adminQuery, invalidate } from '@/lib/query';
import { useCan, useStaff } from '@/lib/staff-context';

export const Route = createFileRoute('/_app/')({
  component: HomePage,
});

const STATUS_EVERY = 30_000;
const QUEUE_EVERY = 60_000;

/** A query's answer as a tile source: hidden without permission, never a silent zero. */
function toSource<T, R>(
  allowed: boolean,
  query: UseQueryResult<T>,
  pick: (data: T) => R,
): Source<R> {
  if (!allowed) return { kind: 'hidden' };
  if (query.data !== undefined) return { kind: 'data', data: pick(query.data) };
  return query.isError ? { kind: 'error' } : { kind: 'loading' };
}

function useHomeData() {
  const canStatus = useCan('ops.status');
  const canIssues = useCan('issues.list');
  const canKyc = useCan('kyc.list');
  const canTransactions = useCan('transactions.list');

  const status = useQuery({
    ...adminQuery('ops.status', {}),
    enabled: canStatus,
    refetchInterval: STATUS_EVERY,
  });
  const issues = useQuery({
    ...adminQuery('issues.list', { view: 'overdue', limit: 1 }),
    enabled: canIssues,
    refetchInterval: QUEUE_EVERY,
  });
  const kyc = useQuery({
    ...adminQuery('kyc.list', {}),
    enabled: canKyc,
    refetchInterval: QUEUE_EVERY,
  });
  const changes = useQuery({
    ...adminQuery('changes.list', { view: 'waiting' }),
    refetchInterval: QUEUE_EVERY,
  });
  const transactions = useQuery({
    ...adminQuery('transactions.list', { limit: 1 }),
    enabled: canTransactions,
    refetchInterval: QUEUE_EVERY,
  });

  const now = useClock();
  const sources: TileSources = {
    now,
    status: toSource(canStatus, status, (d) => d as unknown as OpsStatus),
    issues: toSource(canIssues, issues, (d) => d.counts),
    kyc: toSource(canKyc, kyc, (d) => ({
      waiting: d.rows.length,
      oldestMs: oldestWait(
        d.rows.map((row) => row.in_review_since),
        now,
      ),
    })),
    changes: toSource(true, changes, (d) => ({ waiting: d.counts.waiting })),
    transactions: toSource(canTransactions, transactions, (d) => d.summary),
  };

  const ops = sources.status.kind === 'data' ? sources.status.data : null;
  const statusIssues =
    sources.issues.kind === 'data'
      ? sources.issues.data
      : (ops?.issues ?? null);
  const kycNow = sources.kyc.kind === 'data' ? sources.kyc.data : null;
  const alerts = boardAlerts({
    status: ops,
    issues: statusIssues,
    kyc: kycNow,
    now,
  });
  const unreadable = [
    [canStatus, status],
    [canIssues, issues],
    [canKyc, kyc],
    [true, changes],
    [canTransactions, transactions],
  ].filter(([allowed, q]) => allowed && failed(q as UseQueryResult)).length;
  const queries = [status, issues, kyc, changes, transactions];

  return {
    sources,
    alerts,
    unreadable,
    now,
    status,
    queries,
    updatedAt: status.dataUpdatedAt || changes.dataUpdatedAt,
  };
}

function failed(query: UseQueryResult): boolean {
  return query.isError && query.data === undefined;
}

/** The longest wait among ISO times, in ms; null when there are none. */
function oldestWait(times: string[], now: number): number | null {
  const ages = times.flatMap((t) => ageMs(t, now) ?? []);
  return ages.length ? Math.max(...ages) : null;
}

function HomePage() {
  const staff = useStaff();
  const canStatus = useCan('ops.status');
  const { sources, alerts, unreadable, now, status, queries, updatedAt } =
    useHomeData();
  const ops = sources.status.kind === 'data' ? sources.status.data : null;
  const order = tileOrder(staff.roles, staff.is_super_admin);
  const tiles = buildTiles(sources);
  const visible = order.flatMap((id) => {
    const tile = tiles[id];
    return tile ? [tile] : [];
  });
  const primary = visible.slice(0, 4);
  const rest = visible.slice(4);

  const refreshAll = () =>
    invalidate(
      'ops.status',
      'issues.list',
      'kyc.list',
      'changes.list',
      'transactions.list',
    );
  const fetching = queries.some((q) => q.isFetching);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={`Hello, ${staff.display_name}`}
        description="Is anything wrong, and what needs you."
      />
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1">
          <BoardState alerts={alerts} unreadable={unreadable} />
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <Updated at={updatedAt} />
          <Button
            size="sm"
            variant="outline"
            onClick={() => refreshAll()}
            disabled={fetching}
            aria-busy={fetching || undefined}
          >
            <Icon icon={Refresh01Icon} size={14} />
            Refresh
          </Button>
        </div>
      </div>

      {canStatus ? (
        <QueryState
          query={status}
          label="the status board"
          staleAfterMs={3 * STATUS_EVERY}
          skeleton={<BoardSkeleton />}
        >
          {(data) => (
            <StatusBody data={data as unknown as OpsStatus} now={now} />
          )}
        </QueryState>
      ) : null}

      {primary.length > 0 ? (
        <KpiBand tiles={primary} label="Key figures" />
      ) : null}
      {rest.length > 0 ? <TileGrid tiles={rest} /> : null}

      {ops?.switches ? (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <SwitchStrip switches={ops.switches} />
          <Link
            to="/switches"
            className="text-sm text-foreground underline underline-offset-2"
          >
            Open kill switches
          </Link>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {staff.is_super_admin ? (
          <Badge variant="muted">Super admin</Badge>
        ) : null}
        {staff.roles.map((role) => (
          <Badge key={role} variant="muted" className="capitalize">
            {role}
          </Badge>
        ))}
        {staff.roles.length === 0 && !staff.is_super_admin ? (
          <span className="text-sm text-muted-foreground">
            No roles yet. Ask the super admin.
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** The clock for ages, ticking every 30 s so "52 min ago" stays true. */
function useClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

function Updated({ at }: Readonly<{ at: number }>) {
  if (!at) return null;
  const iso = new Date(at).toISOString();
  return (
    <span title={formatDateTime(iso)}>
      Updated {iso.slice(11, 19)} UTC · every 30 s
    </span>
  );
}

function BoardSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="grid grid-cols-1 overflow-hidden rounded-lg border border-border bg-card sm:grid-cols-2 xl:grid-cols-4"
    >
      {Array.from({ length: 4 }, (_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder cells
        <SkeletonKpi key={index} spark={false} />
      ))}
    </div>
  );
}

/** The integrity strip, from the status read. */
function StatusBody({ data, now }: Readonly<{ data: OpsStatus; now: number }>) {
  return <IntegrityStrip cells={integrityCells(data, now)} />;
}

type CellState = IntegrityCell['state'];

function poolCellState(
  pool: OpsStatus['last_pool_check'],
  now: number,
): CellState {
  if (!pool) return 'unknown';
  if (pool.result === 'mismatch') return 'failed';
  return (ageMs(pool.checked_at, now) ?? 0) > 26 * HOUR ? 'late' : 'ok';
}

/** "ok · 3 hours ago", or `missing` when the job has never reported. */
function jobDetail(
  job: { status: string; at: string | null } | null,
  missing: string,
  now: number,
): string {
  if (!job) return missing;
  if (!job.at) return job.status;
  return `${job.status} · ${relativeTime(job.at, new Date(now))}`;
}

function chainCell(
  chain: OpsStatus['ledger_chain_head'],
  now: number,
): IntegrityCell {
  const base = { id: 'chain', label: 'Ledger chain' };
  if (!chain) return { ...base, state: 'unknown', detail: 'No export yet' };
  if (!chain.chain_ok) {
    return {
      ...base,
      state: 'failed',
      detail: `Broken at entry ${chain.broken_at}`,
      at: chain.exported_at,
    };
  }
  const when = relativeTime(chain.exported_at, new Date(now));
  return {
    ...base,
    state: 'ok',
    detail: `Verified · exported ${when}`,
    at: chain.exported_at,
  };
}

function integrityCells(data: OpsStatus, now: number): IntegrityCell[] {
  const pool = data.last_pool_check;
  return [
    chainCell(data.ledger_chain_head, now),
    {
      id: 'books',
      label: 'Books check',
      state: jobState(data.last_books_check, now, 26 * HOUR),
      detail: jobDetail(data.last_books_check, 'Has not run', now),
      at: data.last_books_check?.at,
    },
    {
      id: 'pool',
      label: 'Pool check',
      state: poolCellState(pool, now),
      detail: pool
        ? `${pool.result} · ${relativeTime(pool.checked_at, new Date(now))}`
        : 'No check yet',
      at: pool?.checked_at,
    },
    {
      id: 'sweeper',
      label: 'Wallet sweeper',
      state: jobState(data.sweeper, now, 30 * MINUTE),
      detail: jobDetail(data.sweeper, 'Has not run', now),
      at: data.sweeper?.at,
    },
  ];
}
